import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  Skeleton,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'; // NEW
import Swal from 'sweetalert2';
import config from '../../Config';
import AddProductForm from './AddProduct';
import EditProductForm from './EditProduct';
import { useNavigate } from 'react-router-dom';

export default function UpsellProductManagement() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [imageErrors, setImageErrors] = useState({});

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.success && Array.isArray(data.products)) {
        setProducts(data.products);
      } else {
        setProducts([]);
      }
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this product?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    setDeletingId(id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product/delete/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire('Deleted!', data.message || 'Product deleted.', 'success');
        fetchProducts();
      } else {
        Swal.fire('Error', data.message || 'Failed to delete product.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Something went wrong.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setShowAddForm(false);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditProduct(null);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageError = (productId) => {
    setImageErrors((prev) => ({ ...prev, [productId]: true }));
  };

  const paginatedProducts = products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold" mb={3}>
          Upsell Products
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isSmDown ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 3,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Paper key={i} sx={{ p: 2, borderRadius: 3 }}>
              <Skeleton variant="rectangular" height={150} />
              <Skeleton sx={{ mt: 2 }} width="60%" />
              <Skeleton width="40%" />
              <Skeleton width="30%" />
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold" mb={3}>
        Upsell Products
      </Typography>

      {showAddForm ? (
        <AddProductForm
          onCancel={handleCancel}
          onSuccess={() => {
            setShowAddForm(false);
            setTimeout(fetchProducts, 500);
          }}
        />
      ) : editProduct ? (
        <EditProductForm
          productData={editProduct}
          onCancel={handleCancel}
          onSuccess={() => {
            setEditProduct(null);
            fetchProducts();
          }}
        />
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" size="large" onClick={() => setShowAddForm(true)}>
              Add New Product
            </Button>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isSmDown ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 3,
            }}
          >
            {paginatedProducts.map((product) => {
              const showImage = product.image && !imageErrors[product._id];

              return (
                <Box
                  key={product._id}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid #ddd',
                    background: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: '0.2s',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => navigate(`/product/${product._id}`)}
                >
                  {showImage ? (
                    <Box
                      component="img"
                      src={product.image}
                      alt={product.name}
                      onError={() => handleImageError(product._id)}
                      sx={{
                        width: '100%',
                        height: 180,
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 180,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        color: 'grey.400',
                      }}
                    >
                      <ImageNotSupportedIcon sx={{ fontSize: 56 }} />
                    </Box>
                  )}

                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 0.5, color: 'primary.main', fontWeight: 600 }}
                    >
                      {product.name}
                    </Typography>

                    <Chip
                      label={product.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={product.isActive ? 'success' : 'default'}
                      sx={{ mb: 1 }}
                    />

                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Category:</strong> {product.category?.name || '-'}
                    </Typography>

                    {product.subCategory?.name && (
                      <Typography variant="body2">
                        <strong>Subcategory:</strong> {product.subCategory.name}
                      </Typography>
                    )}

                    {product.keyFeatures?.filter(Boolean).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Key Features:
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          {product.keyFeatures.filter(Boolean).map((f, i) => (
                            <Chip key={i} label={f} size="small" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 1.5,
                      borderTop: '1px solid #eee',
                      gap: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(product);
                      }}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      disabled={deletingId === product._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(product._id);
                      }}
                    >
                      {deletingId === product._id ? <CircularProgress size={20} /> : <DeleteIcon />}
                    </IconButton>
                  </Box>
                </Box>
              );
            })}

            {products.length === 0 && (
              <Typography variant="h6" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                No products found.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <TablePagination
            component="div"
            count={products.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[6, 9, 12]}
          />
        </>
      )}
    </Box>
  );
}
